import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  ListSubheader,
} from "@mui/material";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { formatLastFirst } from "../../../../utils/name";
import { isInstructorInactive } from "../../../../utils/instructorStatus";

const InstructorDropdown = ({
  value,
  onChange,
  name = "InstructorID",
  label = "Instructor ID",
  fullWidth = true,
  margin = "normal",
  required = false,
  disabled = false,
  activeOnly = false,
  ...otherProps
}) => {
  const [activeInstructors, setActiveInstructors] = useState([]);
  const [inactiveInstructors, setInactiveInstructors] = useState([]);
  const [archivedInstructors, setArchivedInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch instructors from users collection. Users switched to
        // Inactive on the instructor page (Active: "0") still live here, so
        // they're routed into the inactive list rather than shown as active.
        const activeQuery = query(
          collection(db, "users"),
          where("userTypes.instructor", "==", true),
        );

        const activeSnapshot = await getDocs(activeQuery);
        const activeList = [];
        const inactiveUserList = [];

        activeSnapshot.forEach((doc) => {
          const userData = doc.data();

          // Debug: Log the first few users to see their structure
          if (activeList.length < 3) {
            console.log("Sample active user data:", {
              docId: doc.id,
              userData: userData,
              hasInstructorID: !!userData.InstructorID,
              InstructorID: userData.InstructorID,
            });
          }

          // Check if user has InstructorID field, if not, use document ID as fallback
          let instructorId = userData.InstructorID;
          if (!instructorId) {
            // Fallback to document ID if InstructorID field doesn't exist
            instructorId = doc.id;
            console.warn(
              "User missing InstructorID field, using doc ID:",
              doc.id,
              userData.name,
            );
          }

          const inactive = isInstructorInactive(userData);
          (inactive ? inactiveUserList : activeList).push({
            id: String(instructorId),
            name: userData.name || "Unknown Name",
            displayName: formatLastFirst(userData) || "Unknown Name",
            email: userData.email || "",
            status: inactive ? "inactive" : "active",
          });
        });

        // Fetch archived instructors (the Instructor Archive tab). These
        // aren't offered as choices — they're only kept so a record already
        // assigned to one still shows that instructor's name.
        const archivedSnapshot = await getDocs(
          collection(db, "InstructorArchive"),
        );
        const archivedList = [];

        archivedSnapshot.forEach((doc) => {
          const instructorData = doc.data();
          // Both collections have InstructorID field - use that as the key
          if (instructorData.InstructorID) {
            const archivedName =
              instructorData.name || instructorData.instructor_name || "";
            archivedList.push({
              id: String(instructorData.InstructorID), // Use InstructorID field
              name:
                instructorData.name ||
                instructorData.instructor_name ||
                "Unknown Name",
              displayName:
                formatLastFirst({ ...instructorData, name: archivedName }) ||
                "Unknown Name",
              email: instructorData.email || "",
              status: "archived",
            });
          }
        });

        // Drop archive entries for anyone still in users so the same
        // InstructorID doesn't render twice (which confuses MUI's Select
        // display label, causing an active pick to show the wrong section).
        const listedIds = new Set(
          [...activeList, ...inactiveUserList].map((i) => i.id),
        );
        const dedupedArchivedList = archivedList.filter(
          (i) => !listedIds.has(i.id),
        );

        // Sort both lists by "Lastname, Firstname" to match the labels shown
        activeList.sort((a, b) => a.displayName.localeCompare(b.displayName));
        inactiveUserList.sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );

        // Debug logging
        console.log("=== InstructorDropdown Fetch Results ===");
        console.log("Active instructors found:", activeList.length);
        console.log("Inactive instructors found:", inactiveUserList.length);
        console.log("Archived instructors found:", dedupedArchivedList.length);
        console.log("Sample active instructor:", activeList[0]);
        console.log("Sample inactive instructor:", inactiveUserList[0]);
        console.log(
          "Active instructor IDs:",
          activeList.map((i) => i.id),
        );
        console.log(
          "Inactive instructor IDs:",
          inactiveUserList.map((i) => i.id),
        );

        setActiveInstructors(activeList);
        setInactiveInstructors(inactiveUserList);
        setArchivedInstructors(dedupedArchivedList);
      } catch (err) {
        console.error("Error fetching instructors:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructors();
  }, []);

  const handleChange = (event) => {
    if (onChange) {
      // Create a synthetic event-like object to match the expected format
      const syntheticEvent = {
        target: {
          name: name,
          value: event.target.value,
        },
      };
      onChange(syntheticEvent);
    }
  };

  // An archived instructor only appears when it's the one already assigned
  // to this record. Match by id or by name (fallback for legacy data that
  // stored names instead of IDs).
  const assignedArchived =
    value &&
    archivedInstructors.find(
      (i) => String(i.id) === String(value) || i.name === value,
    );

  // Check if current value exists in any listed instructor
  const allInstructors = [
    ...activeInstructors,
    ...(activeOnly ? [] : inactiveInstructors),
    ...(assignedArchived ? [assignedArchived] : []),
  ];
  const currentValueExists =
    value &&
    allInstructors?.some((instructor) => {
      return (
        String(instructor.id) === String(value) || instructor.name === value
      );
    });

  // Resolve the actual stored value to the matching instructor id for the Select
  const resolvedValue = React.useMemo(() => {
    if (!value) return "";
    const byId = allInstructors.find((i) => String(i.id) === String(value));
    if (byId) return String(byId.id);
    const byName = allInstructors.find((i) => i.name === value);
    if (byName) return String(byName.id);
    return "";
  }, [value, allInstructors]);

  // Enhanced debugging for missing instructor IDs
  const showMissingIdWarning =
    value && !currentValueExists && allInstructors.length > 0;

  // Simple debug logging
  if (value && allInstructors.length > 0) {
    console.log(`InstructorDropdown Debug:`, {
      searchingFor: value,
      searchType: typeof value,
      found: currentValueExists,
      totalInstructors: allInstructors.length,
      sampleIds: allInstructors
        .slice(0, 5)
        .map((i) => ({ id: i.id, name: i.name })),
    });
  }

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} margin={margin} disabled>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} disabled {...otherProps}>
          <MenuItem value="">
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8 }}>Loading instructors...</span>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth={fullWidth} margin={margin} disabled>
        <InputLabel error>{label}</InputLabel>
        <Select value="" label={label} error disabled {...otherProps}>
          <MenuItem value="">Error loading instructors</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      margin={margin}
      required={required}
      disabled={disabled}
      error={showMissingIdWarning}
    >
      <InputLabel error={showMissingIdWarning}>
        {label}
        {showMissingIdWarning && " (ID not found)"}
      </InputLabel>
      <Select
        value={resolvedValue}
        label={label}
        onChange={handleChange}
        name={name}
        error={showMissingIdWarning}
        {...otherProps}
      >
        <MenuItem value="">
          <em>Select an Instructor</em>
        </MenuItem>
        {showMissingIdWarning && (
          <MenuItem value={String(value)} disabled style={{ color: "red" }}>
            ⚠️ {value} (ID not found in instructor list)
          </MenuItem>
        )}

        {/* Active Instructors Section */}
        {activeInstructors.length > 0 && (
          <ListSubheader sx={{ color: "primary.main", fontWeight: "bold" }}>
            Active Instructors
          </ListSubheader>
        )}
        {activeInstructors.map((instructor) => (
          <MenuItem
            key={`active-${instructor.id}`}
            value={String(instructor.id)}
          >
            {instructor.displayName}
          </MenuItem>
        ))}

        {/* Inactive Instructors Section — hidden when activeOnly */}
        {!activeOnly && inactiveInstructors.length > 0 && (
          <ListSubheader sx={{ color: "text.secondary", fontWeight: "bold" }}>
            Inactive Instructors
          </ListSubheader>
        )}
        {!activeOnly &&
          inactiveInstructors.map((instructor) => (
            <MenuItem
              key={`inactive-${instructor.id}`}
              value={String(instructor.id)}
            >
              <span style={{ color: "#666", fontStyle: "italic" }}>
                {instructor.displayName} (Inactive)
              </span>
            </MenuItem>
          ))}

        {/* Archived instructor — only the one already assigned, if any */}
        {assignedArchived && (
          <ListSubheader sx={{ color: "text.secondary", fontWeight: "bold" }}>
            Archived Instructor
          </ListSubheader>
        )}
        {assignedArchived && (
          <MenuItem
            key={`archived-${assignedArchived.id}`}
            value={String(assignedArchived.id)}
          >
            <span style={{ color: "#666", fontStyle: "italic" }}>
              {assignedArchived.displayName} (Archived)
            </span>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default InstructorDropdown;
